import { useEffect, useState } from "react";

import type { TotpSetupResponse } from "@quality-field/shared";
import { AlertTriangle, Check, Copy, Download, Loader2 } from "lucide-react";

import { CodeInput } from "@/components/auth/CodeInput";
import { GradientScene, SceneError, SceneStep } from "@/components/auth/GradientScene";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/lib/api-client";

/**
 * Authenticator enrolment.
 *
 * ⚠ DELIBERATE DEVIATION from mockups/02-2fa-onboarding.html, which shows the recovery
 * codes at step 3 and the six-digit confirmation at step 4.
 *
 * That order cannot be built: the server mints recovery codes inside /auth/totp/confirm,
 * as part of proving the authenticator actually works. Showing them first would mean
 * handing someone ten permanent sign-in codes for an authenticator nobody has verified
 * they can use — and if they had mis-scanned, those codes would be the only way back into
 * an account they could not otherwise reach.
 *
 * So the order here is scan → confirm → recovery codes. Worth re-confirming with Ubi, but
 * the mockup's ordering is the one that should move.
 */
export function TotpSetup() {
  const { totpSetup, totpConfirm } = useAuth();
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [phase, setPhase] = useState<"scan" | "confirm" | "codes">("scan");
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    totpSetup()
      .then((data) => {
        if (!cancelled) setSetup(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Could not start enrolment. Reload and try again.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [totpSetup]);

  async function confirm(submitted: string) {
    if (!setup || submitted.length !== 6) return;
    setError("");
    setBusy(true);
    try {
      const codes = await totpConfirm(submitted, setup.secret);
      // ⚠ The one and only moment these exist in plaintext. Hold them in state and show
      // them BEFORE letting the user through — the session is already live at this point,
      // so nothing else forces them to stop and read.
      setRecoveryCodes(codes);
      setPhase("codes");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not verify the code. Try again.");
      setCode("");
      setBusy(false);
    }
  }

  function copySecret() {
    if (!setup) return;
    void navigator.clipboard.writeText(setup.secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function downloadCodes() {
    const body = [
      "Quality Field recovery codes",
      setup ? `Account: ${setup.accountName}` : "",
      `Generated: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`,
      "",
      "Each code signs you in once if you lose your authenticator.",
      "Keep them somewhere only you can reach.",
      "",
      ...recoveryCodes,
    ].join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "quality-field-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Recovery codes ──
  if (phase === "codes") {
    return (
      <GradientScene>
        <div>
          <SceneStep label="Step 3 of 3 — Recovery codes" />
          <h2 className="mt-2 text-xl font-bold text-white">Save these somewhere safe</h2>
        </div>

        <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/30 bg-amber-400/15 px-3.5 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" />
          <p className="text-xs text-amber-100">
            Shown once. Each one signs you in if you lose your phone — they cannot be retrieved
            again, only replaced.
          </p>
        </div>

        <div className="mono grid grid-cols-2 gap-2 text-sm text-white/90">
          {recoveryCodes.map((c) => (
            <span key={c} className="rounded-md border border-white/15 bg-white/10 px-3 py-1.5">
              {c}
            </span>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={downloadCodes}
            className="glass-outline-btn flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium text-white/90"
          >
            <Download className="h-4 w-4" /> Download
          </button>
          <button
            type="button"
            onClick={() => window.location.replace("/")}
            className="gradient-btn flex-1 rounded-lg py-2.5 text-sm font-semibold shadow-lg"
          >
            I have saved them
          </button>
        </div>
      </GradientScene>
    );
  }

  // ── Scan and confirm ──
  return (
    <GradientScene>
      <div>
        <SceneStep label={phase === "scan" ? "Step 2 of 3 — Two-factor authentication" : "Step 2 of 3 — Confirm the code"} />
        <h2 className="mt-2 text-xl font-bold text-white">
          {phase === "scan" ? "Set up your authenticator" : "Enter the six-digit code"}
        </h2>
      </div>

      <SceneError message={error} />

      {!setup && !error && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-white/60" />
        </div>
      )}

      {setup && phase === "scan" && (
        <>
          <p className="text-sm text-white/70">
            Scan this code with Microsoft Authenticator, Google Authenticator or any TOTP app.
          </p>
          <div className="flex justify-center">
            <div className="rounded-xl bg-white p-3">
              {/* The server renders the QR and returns a data URL, so the secret never has
                  to be turned into an image by the browser. */}
              <img src={setup.qrCodeDataUrl} alt="Authenticator QR code" width={168} height={168} />
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45">
              Or enter this key by hand
            </p>
            <div className="flex items-center gap-2">
              <code className="mono flex-1 break-all rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm tracking-widest text-white/90">
                {setup.secret}
              </code>
              <button
                type="button"
                onClick={copySecret}
                title="Copy"
                aria-label="Copy the setup key"
                className="glass-outline-btn rounded-lg p-2.5 text-white/80"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPhase("confirm")}
            className="gradient-btn w-full rounded-lg py-2.5 text-sm font-semibold shadow-lg"
          >
            I have scanned it
          </button>
        </>
      )}

      {setup && phase === "confirm" && (
        <>
          <p className="text-sm text-white/70">
            Enter the six-digit code your authenticator is showing for {setup.accountName}.
          </p>
          <CodeInput value={code} onChange={setCode} onComplete={confirm} disabled={busy} autoFocus />
          <button
            type="button"
            disabled={busy || code.length !== 6}
            onClick={() => confirm(code)}
            className="gradient-btn flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Verifying…" : "Finish setup"}
          </button>
          <button
            type="button"
            onClick={() => {
              setPhase("scan");
              setCode("");
              setError("");
            }}
            className="w-full text-center text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
          >
            Back to the QR code
          </button>
        </>
      )}
    </GradientScene>
  );
}
